import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useUserStore} from '@/entities/User/store/userStoreProvider';
import type {LoginDto, LoginResponseDto} from '../api/auth.repository';
import {AuthService} from '../model/auth.service';

export type AuthError = {
  message: string;
  code?: string;
  status?: number;
};

export type AuthResult = {success: true; data: LoginResponseDto} | {success: false; error: string};

export const useAuth = () => {
  const queryClient = useQueryClient();
  const setUser = useUserStore(store => store.setUser);

  const loginMutation = useMutation<LoginResponseDto, AuthError, LoginDto>({
    mutationFn: async (credentials: LoginDto) => {
      try {
        const response = await AuthService.login(credentials);
        return response;
      } catch (err: unknown) {
        const error = err as {
          response?: {data?: {message?: string; code?: string}; status?: number};
          message?: string;
          code?: string;
          status?: number;
        };
        const errorMessage = error?.response?.data?.message || error?.message || 'Произошла ошибка при входе';

        throw {
          message: errorMessage,
          code: error?.response?.data?.code || error?.code,
          status: error?.response?.status || error?.status,
        } as AuthError;
      }
    },
    onSuccess: data => {
      // Если API вернул профиль пользователя, сразу устанавливаем его в store и cache
      if (data.user?.profile) {
        setUser(data.user.profile);
        queryClient.setQueryData(['user'], data.user.profile);
      }
      // Принудительно перезапрашиваем данные пользователя
      queryClient.refetchQueries({queryKey: ['user']});
    },
  });

  const login = async (credentials: LoginDto): Promise<AuthResult> => {
    try {
      const data = await loginMutation.mutateAsync(credentials);
      // Возвращаем успешный результат
      return {success: true, data};
    } catch (err: unknown) {
      // Возвращаем ошибку как часть результата вместо throw
      const error = err as {message?: string};
      return {success: false, error: error?.message || 'Произошла ошибка при входе'};
    }
  };

  return {
    login,
    loginMutation,
    isLoading: loginMutation.isPending,
    isSuccess: loginMutation.isSuccess,
    isError: loginMutation.isError,
    error: loginMutation.error,
    data: loginMutation.data,
    reset: loginMutation.reset,
  };
};
